import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { optimizationPricesResultSelector } from '../../../../reducers/OtimizacaoPrecos';
import {
  resetHistoryPagination,
  setHistoryBigNumbers,
  setHistoryHistogramChart,
  setHistoryHistogramTableData,
  setHistoryPagination,
  setHistoryPeriodFilter,
  setHistoryStoresIdFilter,
} from '../../../../reducers/OtimizacaoPrecos/optimizationPricesResult';
import { snakeCaseToCamelCase } from '../../../../utils/snakeCaseToCamelCase';
import { downloadOptimization, getOptimizationHistoryBigNumbers, getOptimizationHistoryHistogramChart, getOptimizationHistoryTableData } from '../services';

export const sortOptionsDatakeyMap: Record<string, string> = {
  productId: 'product',
  description: 'product',
  productFamilyId: 'product',
};

const sortOptions = {
  product: [
    {
      value: 'productId',
      label: 'Código do produto',
    },
    {
      value: 'description',
      label: 'Descrição do produto',
    },
    {
      value: 'productFamilyId',
      label: 'Código da família',
    },
  ],
};

const initialDataKeysState = {
  product: sortOptionsDatakeyMap.productId,
};

export function useHistoryPrices() {
  const { id } = useParams() as Record<'id', string>;

  const [datakeys, setDatakeys] = useState(initialDataKeysState);

  const dispatch = useDispatch();
  const { result, tabs, header } = useSelector(optimizationPricesResultSelector, shallowEqual);
  const { stores } = result;
  const { filters } = tabs.history;

  const storesId = stores.map(store => store.storeId);

  const hasDates = filters.periodSelected?.length === 2;

  const { isFetching: isFetchingHistoryBigNumbers } = useQuery({
    queryKey: ['optimization-prices-history-tab-big-numbers', id, filters.storesSelected, filters.periodSelected?.[0], filters.periodSelected?.[1]],
    retry: true,
    keepPreviousData: true,
    enabled: !!id && filters.isCompleted && hasDates,
    queryFn: () =>
      getOptimizationHistoryBigNumbers({
        id,
        optimization_stores_id: filters.storesSelected?.length > 0 ? filters.storesSelected : storesId,
        start_date: filters.periodSelected?.[0] as Date,
        end_date: filters.periodSelected?.[1] as Date,
      }),
    onSuccess(data) {
      if (!data) return;

      dispatch(
        setHistoryBigNumbers({
          data: {
            adhesion: data.optimization_adherence_percentage,
            pricesApplied: data.total_applied_prices,
          },
        }),
      );
    },
  });

  const { isFetching: isFetchingHistoryChart, data: historyChart } = useQuery({
    queryKey: [
      'optimization-prices-history-tab-histogram-chart',
      id,
      filters.storesSelected,
      filters.periodSelected?.[0],
      filters.periodSelected?.[1],
      filters.productsFamilyId,
      filters.productsId,
      filters.responsibleId,
    ],
    retry: true,
    keepPreviousData: true,
    enabled: !!id && filters.isCompleted && hasDates,
    queryFn: () =>
      getOptimizationHistoryHistogramChart({
        id,
        end_date: filters.periodSelected?.[1] as Date,
        start_date: filters.periodSelected?.[0] as Date,
        families_id: filters.productsFamilyId,
        products_id: filters.productsId,
        optimization_stores_id: filters.storesSelected?.length > 0 ? filters.storesSelected : storesId,
        responsibles: filters.responsibleId,
      }),
    onSuccess(data) {
      if (!data) return;

      const series = [
        {
          name: 'Qtd preço sugerido',
          data: [] as number[],
        },
        {
          name: 'Qtd preço aplicado',
          data: [] as number[],
        },
      ];

      const histogramCategories = data.values.map(item => {
        const start = format(new Date(`${item.start_date}T12:00:00`), 'dd/MM');
        const end = format(new Date(`${item.end_date}T12:00:00`), 'dd/MM');

        series[0].data.push(item.quantity_suggested_prices);
        series[1].data.push(item.quantity_applied_prices);

        return `${start} ~ ${end}`;
      });

      dispatch(setHistoryHistogramChart({ data: { categories: histogramCategories, series: series } }));
    },
  });

  const { isFetching: isFetchingHistoryTableData } = useQuery({
    queryKey: [
      'optimization-prices-history-tab-table-data',
      id,
      filters.storesSelected,
      filters.periodSelected?.[0],
      filters.periodSelected?.[1],
      filters.productsFamilyId,
      filters.productsId,
      filters.responsibleId,
      tabs.history.pagination.page,
      tabs.history.pagination.size,
      tabs.history.pagination.sortColumn,
      tabs.history.pagination.orderBy,
    ],
    retry: true,
    keepPreviousData: true,
    enabled: !!id && filters.isCompleted && hasDates,
    queryFn: () =>
      getOptimizationHistoryTableData({
        id,
        end_date: filters.periodSelected?.[1] as Date,
        start_date: filters.periodSelected?.[0] as Date,
        families_id: filters.productsFamilyId,
        products_id: filters.productsId,
        optimization_stores_id: filters.storesSelected?.length > 0 ? filters.storesSelected : storesId,
        responsibles: filters.responsibleId,
        page: tabs.history.pagination.page,
        size: tabs.history.pagination.size,
        sort: tabs.history.pagination.sortColumn,
        order: tabs.history.pagination?.orderBy ?? 'asc',
      }),
    onSuccess(data) {
      if (!data) return;

      const { content, ...paginationRest } = snakeCaseToCamelCase(data);

      const pagination = {
        page: paginationRest.number,
        size: paginationRest.size,
        sortColumn: tabs.history.pagination.sortColumn,
        orderBy: tabs.history.pagination?.orderBy ?? 'asc',
        pageSize: paginationRest.pageable.pageSize,
        numberOfElements: paginationRest.numberOfElements,
        totalPages: paginationRest.totalPages,
        totalElements: paginationRest.totalElements,
      };

      dispatch(setHistoryHistogramTableData({ data: content, pagination }));
    },
  });

  const handleSelectStoreId = useCallback(
    (storesIdSelected: string[]) => {
      dispatch(setHistoryStoresIdFilter({ data: storesIdSelected }));
      dispatch(resetHistoryPagination());
    },
    [dispatch],
  );

  const handleSelectPeriodFilter = useCallback(
    (dates: Date[] | null) => {
      dispatch(setHistoryPeriodFilter({ data: dates }));
      dispatch(resetHistoryPagination());
    },
    [dispatch],
  );

  const handleSetSortColumn = useCallback(
    async (column: string, type: 'asc' | 'desc') => {
      dispatch(setHistoryPagination({ pagination: { sortColumn: column, orderBy: type } }));
    },
    [dispatch],
  );

  const onChangeSort = useCallback(
    (_dataKey: string, column: string) => {
      const dataKeyPropertyName = sortOptionsDatakeyMap[column];

      setDatakeys(prev => ({ ...prev, [dataKeyPropertyName]: column }));
      handleSetSortColumn(column, tabs.history.pagination.orderBy as 'asc' | 'desc');
    },
    [handleSetSortColumn, tabs.history.pagination.orderBy],
  );

  const handleChangePage = useCallback(
    (value: number) => {
      dispatch(setHistoryPagination({ pagination: { page: value - 1 } }));
    },
    [dispatch],
  );

  const handleChangeSize = useCallback(
    (value: number) => {
      dispatch(setHistoryPagination({ pagination: { size: value, page: 0 } }));
    },
    [dispatch],
  );

  const handleDownload = useCallback(
    (fileType: 'csv' | 'xlsx') => {
      downloadOptimization({
        id,
        file_type: fileType,
        content: tabs.history.table.map(item => {
          return {
            product: {
              product_id: item.product.productId,
              product_name: item.product.productName,
            },
            store: {
              store_id: item.store.storeId,
              store_name: item.store.storeName,
            },
            family_id: item.familyId,
            date: item.date,
            previous_price: item.previousPrice,
            suggested_price: item.suggestedPrice,
            variation: item.variation,
            variation_percentage: item.variationPercentage,
            status_optimization: item.statusOptimization,
            responsible: item.responsible,
          };
        }),
      });
    },
    [id, tabs.history.table],
  );

  return {
    handlers: {
      handleSelectStoreId,
      handleSelectPeriodFilter,
      handleChangeSize,
      handleChangePage,
      handleSetSortColumn,
      handleDownload,
      onChangeSort,
    },
    states: {
      isLoadingHistoryBigNumbers: isFetchingHistoryBigNumbers,
      isFetchingHistoryChart,
      isFetchingHistoryTableData,
      history: tabs.history,
      stores: result.stores,
      optimizationStatus: {
        start: header.validity.start,
        end: header.validity.end || new Date(),
        active: header.status.active,
      },
      isCompletedFilter: filters.isCompleted,
      tablePagination: tabs.history.pagination,
      tableData: tabs.history.table,
      storesIdWithoutResults: result.storesIdWithoutResults,
      hasHistoryPricesData: !!historyChart,
      isLoadingHistoryPricesData: isFetchingHistoryBigNumbers || isFetchingHistoryChart || isFetchingHistoryTableData,
      sortOptions,
      datakeys,
    },
  };
}
